import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Nav, Tab, Button, Card, Col, Row, Table, Modal, Spinner, Form, Badge, Alert, Dropdown, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faRobot, 
    faChartLine, 
    faUsers, 
    faBoxOpen, 
    faClock, 
    faCog, 
    faPlay,
    faSync,
    faEye,
    faUserTie,
    faHandshake,
    faBullseye,
    faEnvelope,
    faPhone,
    faDollarSign,
    faNetworkWired,
    faDownload,
    faPlus,
    faTurnUp,
    faTurnDown,
    faExclamationTriangle,
    faInfoCircle,
    faChartPie,
    faChartBar,
    faAward,
    faCheck,
    faPause,
    faComments,
    faUser,
    faHistory,
    faArrowTrendUp,
    faThLarge,
    faGlobe,
    faBoltLightning,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
    FunnelChart,
    Funnel,
    LabelList,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar
} from 'recharts';
import { useUserContext } from '../../layouts/UserContext';
import WidgetsSectionTitle from '../modules/widgets/WidgetsSectionTitle';

// CORRECCIÓN: Mantener imports originales
import { 
  SellerbotService, 
  useSellerbotStatus, 
  useSellerbotDashboard,
  useSellerbotConversations,
  useSellerbotProfiles,
  useSellerbotProducts,
  useSellerbotCampaigns,
  useSellerbotRoutines,
  formatCurrency,
  formatNumber,
  formatDate,
  getStageColor,
  translateStage,
  getFormattedValue
} from '../../service/sellerbotService';

// CORRECCIÓN: Importar servicio de conversaciones reales
import { SellerConvService } from '../../service/sellerConvService';

import * as THREE from 'three';
import ConversationNetwork from './ConversationNetwork';
import ConversationNetwork3D from './ConversationNetwork3D';
import ProductsTab from './ProductsTab';
import AgentsTab from './AgentsTab';
import CampaignsTab from './CampaignsTab';
import ContactsTab from './ContactsTab';
import RoutinesTab from './RoutinesTab';
import SettingsTab from './SettingsTab';

type LanguageType = 'es' | 'en';

// ==================== 🎯 INTERFACES ====================

interface ConversationNetwork3DProps {
    conversations: any[];
    profiles: any[];
    products: any[];
    language: 'es' | 'en';
    onNodeClick: (nodeData: any) => void;
}

interface SalesConversation {
    id: string;
    contactName: string;
    contactPhone: string;
    contactEmail?: string;
    profileUsed: string;
    productOffered: string;
    stage: 'prospecting' | 'interest' | 'negotiation' | 'closing' | 'closed' | 'lost';
    lastMessage: string;
    lastMessageTime: string;
    value: number;
    source: string;
    messages: ConversationMessage[];
    notes: string;
    probability: number;
    whatsappChatId?: string;
    isActive?: boolean;
}

interface ConversationMessage {
    id: string;
    sender: 'bot' | 'client';
    message: string;
    timestamp: string;
    type: 'text' | 'image' | 'document';
}

interface DashboardMetrics {
    totalRevenue: number;
    totalLeads: number;
    conversionRate: number;
    activeDeals: number;
    closedDeals: number;
    averageDealValue: number;
    responseTime: number;
    customerSatisfaction: number;
}

interface ChartData {
    date: string;
    revenue: number;
    leads: number;
    conversions: number;
    deals: number;
}

// ==================== 🎨 ESTILOS CSS MEJORADOS ====================
const enhancedStyles = `
  .aiseller-dashboardx {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    min-height: 100vh;
    padding: 20px 0;
  }

  .dashboard-containerx {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
    overflow: hidden;
    margin: 0 auto;
    max-width: 1400px;
  }

  /* KPI Cards Mejoradas */
  .kpi-card-enhancedx {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: none !important;
    border-radius: 16px !important;
    box-shadow: 0 8px 32px rgba(0,0,0,0.08) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    border-left: 4px solid transparent !important;
  }

  .kpi-card-enhancedx::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #0d6efd, #6f42c1);
  }

  .kpi-card-enhancedx:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15) !important;
    border-left-color: var(--bs-primary) !important;
  }

  .kpi-icon-enhanced {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #0d6efd, #6f42c1);
    color: white;
    font-size: 24px;
    margin-bottom: 16px;
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  }

  .kpi-value-enhanced {
    font-size: 2.2rem;
    font-weight: 800;
    color: #2c3e50;
    margin-bottom: 8px;
    line-height: 1;
  }

  .kpi-label-enhanced {
    font-size: 0.85rem;
    color: #6c757d;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
  }

  .kpi-change-enhanced {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .kpi-change-positive {
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
  }

  .kpi-change-negative {
    background: linear-gradient(135deg, #dc3545, #fd7e14);
    color: white;
  }

  /* Gráficos Modernos */
  .chart-container-modern {
    background: white;
    border-radius: 20px;
    padding: 30px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.08);
    border: 1px solid rgba(0,0,0,0.05);
    transition: all 0.3s ease;
    margin-bottom: 24px;
  }

  .chart-container-modern:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(0,0,0,0.12);
  }

  .chart-header-modern {
    border-bottom: 2px solid #f8f9fa;
    padding-bottom: 20px;
    margin-bottom: 25px;
  }

  .chart-title-modern {
    font-size: 1.4rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .chart-subtitle-modern {
    color: #6c757d;
    font-size: 0.95rem;
    margin: 0;
  }

  /* Tabla de Conversaciones Limpia */
  .conversations-table-clean {
    background: white;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0,0,0,0.08);
    border: 1px solid rgba(0,0,0,0.05);
  }

  .conversations-table-clean .table tbody tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f8f9fa;
  }

  .conversations-table-clean .table tbody tr:hover {
    background-color: #f8f9fa;
    transform: scale(1.01);
  }

  .conversations-table-clean .table tbody td {
    padding: 18px 16px;
    vertical-align: middle;
    border: none;
  }

  /* Funnel Chart Styles */
  .funnel-container-modern {
    padding: 20px 0;
  }

  .funnel-stage-modern {
    margin-bottom: 20px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 12px;
    border-left: 4px solid transparent;
    transition: all 0.3s ease;
    position: relative;
  }

  .funnel-stage-modern:hover {
    background: #e9ecef;
    border-left-color: var(--bs-primary);
    transform: translateX(8px);
  }

  .funnel-stage-modern::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(135deg, #0d6efd, #6f42c1);
    border-radius: 0 4px 4px 0;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .funnel-stage-modern:hover::before {
    opacity: 1;
  }

  /* Contact Avatar */
  .contact-avatar-modern {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0d6efd, #6f42c1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 16px;
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
    margin-right: 12px;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .aiseller-dashboard {
      padding: 10px;
    }
    
    .dashboard-container {
      margin: 0 10px;
      border-radius: 15px;
    }
    
    .kpi-card-enhanced:hover,
    .chart-container-modern:hover {
      transform: none;
    }
    
    .kpi-value-enhanced {
      font-size: 1.8rem;
    }
    
    .nav-pills-enhanced .nav-link {
      padding: 8px 16px;
      margin-right: 4px;
      font-size: 0.9rem;
    }
  }

  /* Animaciones */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .fade-in-up {
    animation: fadeInUp 0.6s ease-out;
  }

  .fade-in-up-delay-1 { animation: fadeInUp 0.6s ease-out 0.1s both; }
  .fade-in-up-delay-2 { animation: fadeInUp 0.6s ease-out 0.2s both; }
  .fade-in-up-delay-3 { animation: fadeInUp 0.6s ease-out 0.3s both; }
  .fade-in-up-delay-4 { animation: fadeInUp 0.6s ease-out 0.4s both; }
`;

// ==================== 🎯 COMPONENTE PRINCIPAL ====================
const AISeller: React.FC = () => {
    const [globalAutoRefresh, setGlobalAutoRefresh] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const preventUpdates = useRef(false);
    const { user, isLoading: isUserLoading } = useUserContext();
    const [language, setLanguage] = useState<LanguageType>('es');
    const [activeTab, setActiveTab] = useState('dashboard');
    const [timeFilter, setTimeFilter] = useState('7d');
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showConversationModal, setShowConversationModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);

    // Estados para datos de pestañas
    const [productsFromTab, setProductsFromTab] = useState<any[]>([]);
    const [agentsFromTab, setAgentsFromTab] = useState<any[]>([]);
    const [campaignsFromTab, setCampaignsFromTab] = useState<any[]>([]);
    const [contactsFromTab, setContactsFromTab] = useState<any[]>([]);
    const [routinesFromTab, setRoutinesFromTab] = useState<any[]>([]);

    // CORRECCIÓN: Hook personalizado para conversaciones reales
    const [realConversations, setRealConversations] = useState<any[]>([]);
    const [realConversationsLoading, setRealConversationsLoading] = useState(true);
    const [realConversationsError, setRealConversationsError] = useState<string | null>(null);

    // ==================== HOOKS DE DATOS ====================
    
    const { 
        status: sellerbotStatus, 
        loading: statusLoading, 
        error: statusError, 
        refetch: refetchStatus 
    } = useSellerbotStatus(globalAutoRefresh);

    const { 
        dashboardData, 
        loading: dashboardLoading, 
        error: dashboardError, 
        refetch: refetchDashboard 
    } = useSellerbotDashboard(timeFilter, globalAutoRefresh && !preventUpdates.current);

    // CORRECCIÓN: Hook original de sellerbotService (mantener para compatibilidad)
    const { 
        conversations: fallbackConversations,
        loading: conversationsLoading,
        error: conversationsError, 
        refetch: refetchConversations 
    } = useSellerbotConversations({ status: 'all', limit: 100 }, globalAutoRefresh);

    const { profiles } = useSellerbotProfiles();
    const { campaigns } = useSellerbotCampaigns();
    const { routines } = useSellerbotRoutines();

    // CORRECCIÓN: Función para obtener conversaciones reales como ConversationNetwork
    const fetchRealConversations = useCallback(async () => {
        try {
            setRealConversationsLoading(true);
            setRealConversationsError(null);
            
            console.log('🔍 Obteniendo conversaciones reales del SellerBot...');
            
            const config = {
                whatsAppServerPort: 8000,
                whatsAppApiPort: 8001,
                mlServerPort: 8002
            };
            
            const sellerConversations = await SellerConvService.getSellerBotConversations(config);
            
            console.log(`✅ Obtenidas ${sellerConversations.length} conversaciones reales`);
            setRealConversations(sellerConversations);
            
        } catch (error: any) {
            console.error('❌ Error obteniendo conversaciones reales:', error);
            setRealConversationsError(error.message || 'Error al obtener conversaciones reales');
        } finally {
            setRealConversationsLoading(false);
        }
    }, []);

    // Función para refrescar conversaciones reales
    const refetchRealConversations = useCallback(async () => {
        await fetchRealConversations();
    }, [fetchRealConversations]);

    //Handle rutinas
    const handleRoutinesChange = useCallback((routines: any[]) => {
        setRoutinesFromTab(routines);
    }, []);

    // ==================== DATOS CALCULADOS ====================

    // CORRECCIÓN: Usar conversaciones reales si están disponibles, sino fallback
    const botConversations = useMemo(() => {
        const conversationsToUse = realConversations.length > 0 ? realConversations : fallbackConversations;
        
        console.log('🔍 Conversaciones para dashboard:', {
            real: realConversations.length,
            fallback: fallbackConversations.length,
            using: conversationsToUse.length
        });
        
        // Mapear a formato estándar
        const conversations = conversationsToUse.map((conv: any) => ({
            id: conv.id,
            contactName: conv.contactName,
            contactPhone: conv.contactPhone,
            contactEmail: conv.contactEmail || '',
            stage: conv.stage || 'prospecting',
            value: conv.value || 25000,
            probability: conv.probability || 50,
            profileUsed: conv.profileUsed || 'consultive',
            productOffered: conv.productOffered || 'Producto/Servicio',
            lastMessage: conv.preview || conv.lastMessage || 'Conversación iniciada',
            lastMessageTime: conv.lastMessageTime || new Date().toISOString(),
            messages: conv.messages || [],
            isActive: conv.isActive || false,
            whatsappChatId: conv.whatsappChatId || conv.id,
            source: conv.source || 'whatsapp_filtered_data',
            notes: conv.notes || ''
        }));
        
        console.log('📊 Conversaciones procesadas para dashboard:', conversations.length);
        return conversations;
    }, [realConversations, fallbackConversations]);

    // Calcular métricas del dashboard
    const dashboardMetrics = useMemo((): DashboardMetrics => {
        const closedDeals = botConversations.filter(c => c.stage === 'closed');
        const totalRevenue = closedDeals.reduce((sum, c) => sum + (c.value || 0), 0);
        const totalLeads = botConversations.length;
        const activeDeals = botConversations.filter(c => ['interest', 'negotiation', 'closing'].includes(c.stage));
        
        return {
            totalRevenue,
            totalLeads,
            conversionRate: totalLeads > 0 ? (closedDeals.length / totalLeads) * 100 : 0,
            activeDeals: activeDeals.length,
            closedDeals: closedDeals.length,
            averageDealValue: closedDeals.length > 0 ? totalRevenue / closedDeals.length : 0,
            responseTime: 2.3, // Simulado
            customerSatisfaction: 4.7 // Simulado
        };
    }, [botConversations]);

    // Datos para gráficos
    const chartData = useMemo((): ChartData[] => {
        const last7Days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - (6 - i));
            return date.toISOString().split('T')[0];
        });

        return last7Days.map(date => {
            const dayConversations = botConversations.filter(c => 
                new Date(c.lastMessageTime).toISOString().split('T')[0] === date
            );
            
            return {
                date,
                revenue: dayConversations
                    .filter(c => c.stage === 'closed')
                    .reduce((sum, c) => sum + (c.value || 0), 0),
                leads: dayConversations.length,
                conversions: dayConversations.filter(c => c.stage === 'closed').length,
                deals: dayConversations.filter(c => ['interest', 'negotiation'].includes(c.stage)).length
            };
        });
    }, [botConversations, timeFilter]);

    // Datos del funnel
    const funnelData = useMemo(() => {
        const stages = ['prospecting', 'interest', 'negotiation', 'closing', 'closed'];
        return stages.map(stage => {
            const stageConversations = botConversations.filter(c => c.stage === stage);
            const value = stageConversations.reduce((sum, c) => sum + (c.value || 0), 0);
            
            return {
                stage: translateStage(stage, language),
                count: stageConversations.length,
                value,
                conversionRate: botConversations.length > 0 ? (stageConversations.length / botConversations.length) * 100 : 0
            };
        });
    }, [botConversations, language]);

    // Datos para gráfico de radar (performance de agentes)
    const agentPerformanceData = useMemo(() => {
        return profiles.slice(0, 5).map(profile => {
            const profileConversations = botConversations.filter(c => c.profileUsed === profile.name);
            const closedDeals = profileConversations.filter(c => c.stage === 'closed');
            
            return {
                agent: profile.name,
                leads: profileConversations.length,
                conversions: closedDeals.length,
                revenue: closedDeals.reduce((sum, c) => sum + (c.value || 0), 0) / 1000, // En miles
                satisfaction: profile.successRate || Math.random() * 100,
                responseTime: 100 - (Math.random() * 50) // Invertido para el radar
            };
        });
    }, [profiles, botConversations]);

    // ==================== FUNCIONES ====================

    const handleProductsChange = useCallback((products: any[]) => {
        setProductsFromTab(products);
    }, []);

    const handleAgentsChange = useCallback((agents: any[]) => {
        setAgentsFromTab(agents);
    }, []);

    const handleCampaignsChange = useCallback((campaigns: any[]) => {
        setCampaignsFromTab(campaigns);
    }, []);

    const handleContactsChange = useCallback((contacts: any[]) => {
        setContactsFromTab(contacts);
    }, []);

    const refreshData = async () => {
        try {
            await Promise.all([
                refetchStatus(),
                refetchDashboard(),
                refetchConversations(),
                refetchRealConversations() // CORRECCIÓN: Incluir conversaciones reales
            ]);
            setSuccess(language === 'es' ? 'Datos actualizados' : 'Data refreshed');
            setTimeout(() => setSuccess(null), 2000);
        } catch (error) {
            setError(language === 'es' ? 'Error al actualizar datos' : 'Error refreshing data');
            setTimeout(() => setError(null), 3000);
        }
    };

    // ==================== EFECTOS ====================

    // CORRECCIÓN: Cargar conversaciones reales al inicio
    useEffect(() => {
        fetchRealConversations();
    }, [fetchRealConversations]);

    // Auto-refresh de conversaciones reales
    useEffect(() => {
        if (globalAutoRefresh) {
            const interval = setInterval(() => {
                fetchRealConversations();
            }, 5 * 60 * 1000); // Cada 5 minutos
            
            return () => clearInterval(interval);
        }
    }, [globalAutoRefresh, fetchRealConversations]);

    useEffect(() => {
        if (!realConversationsLoading && !conversationsLoading) {
            setLastUpdate(new Date());
        }
    }, [realConversations, fallbackConversations, realConversationsLoading, conversationsLoading]);

    // ==================== LOADING STATE ====================

    if (isUserLoading) {
        return (
            <div className="aiseller-dashboard">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted">{language === 'es' ? 'Cargando SalesHub...' : 'Loading SalesHub...'}</p>
                    </div>
                </div>
            </div>
        );
    }

    // ==================== RENDER PRINCIPAL ====================

    return (
        <div className="aiseller-dashboard">
            <style>{enhancedStyles}</style>
            
            <div className="dashboard-container">
                <div className="p-4">
                    <WidgetsSectionTitle
                        title="SalesHub AI - Enterprise Dashboard"
                        subtitle="Advanced AI-powered sales automation with real-time analytics and conversation management"
                        icon={faBoltLightning}
                        className="mb-4"
                    />
                    
                    {/* Alertas globales */}
                    {!sellerbotStatus?.success && (
                        <Alert variant="warning" className="mb-3 border-0" style={{ borderRadius: '12px' }}>
                            <FontAwesomeIcon icon={faNetworkWired} className="me-2" />
                            {language === 'es' 
                                ? 'El servidor de Sales Bot no está disponible. Verificando conexión...' 
                                : 'Sales Bot server is currently unavailable. Checking connection...'}
                        </Alert>
                    )}
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3 border-0" style={{ borderRadius: '12px' }}>
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3 border-0" style={{ borderRadius: '12px' }}>
                            <FontAwesomeIcon icon={faCheck} className="me-2" />
                            {success}
                        </Alert>
                    )}

                    {/* Pestañas de navegación */}
                    <Tab.Container activeKey={activeTab} onSelect={(key: string | null) => key && setActiveTab(key)}>
                        <Nav variant="pills" className="mb-4 nav-pills-enhanced">
        <Nav.Item>
            <Nav.Link eventKey="dashboard">
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                {language === 'es' ? 'Dashboard' : 'Dashboard'}
            </Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="conversations">
                <FontAwesomeIcon icon={faComments} className="me-2" />
                {language === 'es' ? 'Conversaciones' : 'Conversations'}
                {botConversations.length > 0 && (
                    <Badge bg="light" text="dark" className="ms-2">{botConversations.length}</Badge>
                )}
            </Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="products">
                <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                {language === 'es' ? 'Productos' : 'Products'}
                {productsFromTab.length > 0 && (
                    <Badge bg="light" text="dark" className="ms-2">{productsFromTab.length}</Badge>
                )}
            </Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="agents">
                <FontAwesomeIcon icon={faUserTie} className="me-2" />
                {language === 'es' ? 'Agentes' : 'Agents'}
            </Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="campaigns">
                <FontAwesomeIcon icon={faBullseye} className="me-2" />
                {language === 'es' ? 'Campañas' : 'Campaigns'}
            </Nav.Link>
        </Nav.Item>
        {/* ==================== NUEVA PESTAÑA RUTINAS ==================== */}
        <Nav.Item>
            <Nav.Link eventKey="routines">
                <FontAwesomeIcon icon={faClock} className="me-2" />
                {language === 'es' ? 'Rutinas' : 'Routines'}
                {routinesFromTab.filter(r => r.isActive).length > 0 && (
                    <Badge bg="light" text="dark" className="ms-2">
                        {routinesFromTab.filter(r => r.isActive).length}
                    </Badge>
                )}
            </Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="contacts">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                {language === 'es' ? 'Contactos' : 'Contacts'}
            </Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="settings">
                <FontAwesomeIcon icon={faCog} className="me-2" />
                {language === 'es' ? 'Configuración' : 'Settings'}
            </Nav.Link>
        </Nav.Item>
    </Nav>

                        <Tab.Content>
                            {/* ==================== DASHBOARD PRINCIPAL ==================== */}
                            <Tab.Pane eventKey="dashboard">
    {/* Header del Dashboard */}
    <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
            <h2 className="mb-1 fw-bold">
                <FontAwesomeIcon icon={faGlobe} className="me-3 text-primary" />
                {language === 'es' ? 'Dashboard' : 'Executive Dashboard'}
            </h2>
            <p className="text-muted mb-0">
                {language === 'es' ? 'Última actualización:' : 'Last updated:'} 
                <strong className="ms-2">{lastUpdate.toLocaleTimeString()}</strong>
            </p>
        </div>
        <div className="d-flex gap-2">
            <Form.Select 
                size="sm" 
                value={timeFilter} 
                onChange={(e) => setTimeFilter(e.target.value)}
                style={{ width: '140px', borderRadius: '12px' }}
            >
                <option value="24h">{language === 'es' ? '24 horas' : '24 hours'}</option>
                <option value="7d">{language === 'es' ? '7 días' : '7 days'}</option>
                <option value="30d">{language === 'es' ? '30 días' : '30 days'}</option>
                <option value="90d">{language === 'es' ? '90 días' : '90 days'}</option>
            </Form.Select>
            <Button 
                variant="primary" 
                size="sm"
                onClick={refreshData}
                disabled={dashboardLoading || realConversationsLoading}
                style={{ borderRadius: '12px', minWidth: '100px' }}
            >
                <FontAwesomeIcon icon={faSync} className={dashboardLoading || realConversationsLoading ? 'fa-spin me-2' : 'me-2'} />
                {language === 'es' ? 'Actualizar' : 'Refresh'}
            </Button>
        </div>
    </div>

    {dashboardLoading || realConversationsLoading ? (
        <div className="text-center my-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">{language === 'es' ? 'Cargando dashboard...' : 'Loading dashboard...'}</p>
        </div>
    ) : (
        <>
            {/* ==================== FILA 1: 6 KPIs BILLING STYLE ==================== */}
            <Row className="mb-4">
                <Col xl={2} lg={4} md={6} className="mb-3">
                    <Card 
                        className="h-100 border-0 shadow-sm" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'kpi', data: 'totalRevenue', value: dashboardMetrics.totalRevenue })}
                    >
                        <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-primary bg-opacity-10 p-2 rounded">
                                        <FontAwesomeIcon icon={faDollarSign} className="text-primary" />
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h5 className="mb-0 fw-bold">{formatCurrency(dashboardMetrics.totalRevenue)}</h5>
                                    <small className="text-muted">{language === 'es' ? 'Revenue Total' : 'Total Revenue'}</small>
                                    <div className="text-success small">
                                        <FontAwesomeIcon icon={faTurnUp} className="me-1" />
                                        +12.5%
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={2} lg={4} md={6} className="mb-3">
                    <Card 
                        className="h-100 border-0 shadow-sm" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'kpi', data: 'mrr', value: dashboardMetrics.totalRevenue / 12 })}
                    >
                        <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-success bg-opacity-10 p-2 rounded">
                                        <FontAwesomeIcon icon={faChartLine} className="text-success" />
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h5 className="mb-0 fw-bold">{formatCurrency(dashboardMetrics.totalRevenue / 12)}</h5>
                                    <small className="text-muted">MRR</small>
                                    <div className="text-success small">
                                        <FontAwesomeIcon icon={faTurnUp} className="me-1" />
                                        +8.3%
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={2} lg={4} md={6} className="mb-3">
                    <Card 
                        className="h-100 border-0 shadow-sm" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'kpi', data: 'avgDeal', value: dashboardMetrics.averageDealValue })}
                    >
                        <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-info bg-opacity-10 p-2 rounded">
                                        <FontAwesomeIcon icon={faHandshake} className="text-info" />
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h5 className="mb-0 fw-bold">{formatCurrency(dashboardMetrics.averageDealValue)}</h5>
                                    <small className="text-muted">{language === 'es' ? 'Deal Promedio' : 'Avg Deal Size'}</small>
                                    <div className="text-success small">
                                        <FontAwesomeIcon icon={faTurnUp} className="me-1" />
                                        +5.2%
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={2} lg={4} md={6} className="mb-3">
                    <Card 
                        className="h-100 border-0 shadow-sm" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'kpi', data: 'conversion', value: dashboardMetrics.conversionRate })}
                    >
                        <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-warning bg-opacity-10 p-2 rounded">
                                        <FontAwesomeIcon icon={faChartPie} className="text-warning" />
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h5 className="mb-0 fw-bold">{dashboardMetrics.conversionRate.toFixed(1)}%</h5>
                                    <small className="text-muted">{language === 'es' ? 'Conversión' : 'Conversion'}</small>
                                    <div className="text-success small">
                                        <FontAwesomeIcon icon={faTurnUp} className="me-1" />
                                        +2.1%
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={2} lg={4} md={6} className="mb-3">
                    <Card 
                        className="h-100 border-0 shadow-sm" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'kpi', data: 'activeDeals', value: dashboardMetrics.activeDeals })}
                    >
                        <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-secondary bg-opacity-10 p-2 rounded">
                                        <FontAwesomeIcon icon={faUsers} className="text-secondary" />
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h5 className="mb-0 fw-bold">{dashboardMetrics.activeDeals}</h5>
                                    <small className="text-muted">{language === 'es' ? 'Deals Activos' : 'Active Deals'}</small>
                                    <div className="text-success small">
                                        <FontAwesomeIcon icon={faTurnUp} className="me-1" />
                                        +15.7%
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={2} lg={4} md={6} className="mb-3">
                    <Card 
                        className="h-100 border-0 shadow-sm" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'kpi', data: 'velocity', value: Math.round(dashboardMetrics.totalLeads / 30) })}
                    >
                        <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-danger bg-opacity-10 p-2 rounded">
                                        <FontAwesomeIcon icon={faArrowTrendUp} className="text-danger" />
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h5 className="mb-0 fw-bold">{Math.round(dashboardMetrics.totalLeads / 30)}</h5>
                                    <small className="text-muted">{language === 'es' ? 'Velocity (días)' : 'Sales Velocity'}</small>
                                    <div className="text-danger small">
                                        <FontAwesomeIcon icon={faTurnDown} className="me-1" />
                                        -3.2%
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ==================== FILA 2: GRÁFICOS PRINCIPALES VARIADOS ==================== */}
            <Row className="mb-4">
                {/* Revenue Timeline - 60% */}
                <Col xl={7} lg={8} className="mb-4">
                    <Card 
                        className="h-100 border-0 shadow-sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'chart', data: 'revenue_timeline', chartData })}
                    >
                        <Card.Header className="bg-white border-0 pb-0">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-1">
                                        <FontAwesomeIcon icon={faArrowTrendUp} className="text-primary me-2" />
                                        {language === 'es' ? 'Tendencia de Ingresos' : 'Revenue Trend'}
                                    </h5>
                                    <small className="text-muted">{language === 'es' ? 'Ingresos y leads en el tiempo' : 'Revenue and leads over time'}</small>
                                </div>
                                <Button variant="outline-primary" size="sm">
                                    <FontAwesomeIcon icon={faEye} />
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor="#0d6efd" stopOpacity={0.1}/>
                                        </linearGradient>
                                        <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#20c997" stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor="#20c997" stopOpacity={0.1}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={(value) => {
                                            const date = new Date(value);
                                            return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
                                        }}
                                    />
                                    <YAxis />
                                    <Tooltip 
                                        formatter={(value, name) => [
                                            name === 'revenue' ? formatCurrency(value as number) : formatNumber(value as number),
                                            name === 'revenue' ? 'Revenue' : 'Leads'
                                        ]}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke="#0d6efd"
                                        fillOpacity={1}
                                        fill="url(#colorRevenue)"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="leads"
                                        stroke="#20c997"
                                        fillOpacity={1}
                                        fill="url(#colorLeads)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Deal Distribution Donut - 20% */}
                <Col xl={2} lg={2} className="mb-4">
                    <Card 
                        className="h-100 border-0 shadow-sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'chart', data: 'deal_distribution', chartData: funnelData })}
                    >
                        <Card.Header className="bg-white border-0 pb-0">
                            <h6 className="mb-1">
                                <FontAwesomeIcon icon={faChartPie} className="text-info me-2" />
                                {language === 'es' ? 'Distribución' : 'Distribution'}
                            </h6>
                        </Card.Header>
                        <Card.Body className="p-2">
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={funnelData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={40}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="count"
                                    >
                                        {funnelData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={
                                                index === 0 ? '#0d6efd' :
                                                index === 1 ? '#20c997' :
                                                index === 2 ? '#ffc107' :
                                                index === 3 ? '#fd7e14' : '#dc3545'
                                            } />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Conversion Gauge - 20% */}
                <Col xl={3} lg={2} className="mb-4">
                    <Card 
                        className="h-100 border-0 shadow-sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'chart', data: 'conversion_gauge', value: dashboardMetrics.conversionRate })}
                    >
                        <Card.Header className="bg-white border-0 pb-0">
                            <h6 className="mb-1">
                                <FontAwesomeIcon icon={faAward} className="text-success me-2" />
                                {language === 'es' ? 'Performance' : 'Performance'}
                            </h6>
                        </Card.Header>
                        <Card.Body className="text-center">
                            <div className="position-relative d-inline-block">
                                <div 
                                    className="rounded-circle border border-5 d-flex align-items-center justify-content-center"
                                    style={{ 
                                        width: '120px', 
                                        height: '120px',
                                        borderColor: dashboardMetrics.conversionRate > 50 ? '#28a745' : 
                                                   dashboardMetrics.conversionRate > 25 ? '#ffc107' : '#dc3545'
                                    }}
                                >
                                    <div className="text-center">
                                        <h4 className="mb-0 fw-bold">{dashboardMetrics.conversionRate.toFixed(1)}%</h4>
                                        <small className="text-muted">{language === 'es' ? 'Conversión' : 'Conversion'}</small>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3">
                                <small className="text-success">
                                    <FontAwesomeIcon icon={faTurnUp} className="me-1" />
                                    +2.3% vs {language === 'es' ? 'mes anterior' : 'last month'}
                                </small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ==================== FILA 3: ANÁLISIS DETALLADO ==================== */}
            <Row className="mb-4">
                {/* Agent Performance - 33% */}
                <Col xl={4} lg={4} className="mb-4">
                    <Card 
                        className="h-100 border-0 shadow-sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'chart', data: 'agent_performance', chartData: agentPerformanceData })}
                    >
                        <Card.Header className="bg-white border-0 pb-0">
                            <h5 className="mb-1">
                                <FontAwesomeIcon icon={faUserTie} className="text-warning me-2" />
                                {language === 'es' ? 'Performance Agentes' : 'Agent Performance'}
                            </h5>
                            <small className="text-muted">{language === 'es' ? 'Top vendedores' : 'Top performers'}</small>
                        </Card.Header>
                        <Card.Body>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={agentPerformanceData} layout="horizontal">
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis type="category" dataKey="agent" width={60} />
                                    <Tooltip />
                                    <Bar dataKey="conversions" fill="#0d6efd" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Sales Funnel - 33% */}
                <Col xl={4} lg={4} className="mb-4">
                    <Card 
                        className="h-100 border-0 shadow-sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'chart', data: 'sales_funnel', chartData: funnelData })}
                    >
                        <Card.Header className="bg-white border-0 pb-0">
                            <h5 className="mb-1">
                                <FontAwesomeIcon icon={faChartBar} className="text-info me-2" />
                                {language === 'es' ? 'Embudo de Ventas' : 'Sales Funnel'}
                            </h5>
                            <small className="text-muted">{language === 'es' ? 'Pipeline de conversión' : 'Conversion pipeline'}</small>
                        </Card.Header>
                        <Card.Body>
                            <div className="funnel-stages">
                                {funnelData.map((stage, index) => (
                                    <div key={stage.stage} className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="fw-medium">{stage.stage}</span>
                                            <span className="text-muted">{stage.count}</span>
                                        </div>
                                        <ProgressBar 
                                            now={stage.conversionRate} 
                                            variant={
                                                index === 0 ? 'primary' :
                                                index === 1 ? 'success' :
                                                index === 2 ? 'warning' :
                                                index === 3 ? 'info' : 'danger'
                                            }
                                            style={{ height: '8px' }}
                                        />
                                        <div className="d-flex justify-content-between mt-1">
                                            <small className="text-success">{stage.conversionRate.toFixed(1)}%</small>
                                            <small className="text-muted">{formatCurrency(stage.value)}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* ==================== BOTÓN PARA ABRIR LA RED 3D ==================== */}
<Col xl={4} lg={4} className="mb-4">
    <Card 
        className="h-100 border-0 shadow-sm"
        style={{ cursor: 'pointer' }}
        onClick={() => setSelectedItem({ type: 'network3d', data: 'conversation_network' })}
    >
        <Card.Header className="bg-body border-0 pb-0">
            <h5 className="mb-1">
                <FontAwesomeIcon icon={faNetworkWired} className="text-danger me-2" />
                {language === 'es' ? 'Red 3D de Conversaciones' : '3D Conversation Network'}
            </h5>
            <small className="text-muted">{language === 'es' ? 'Visualización interactiva' : 'Interactive visualization'}</small>
        </Card.Header>
        <Card.Body className="text-center">
            <div className="py-4">
                <FontAwesomeIcon icon={faNetworkWired} size="3x" className="text-primary mb-3 opacity-50" />
                <p className="text-muted mb-3">
                    {language === 'es' 
                        ? 'Explora las conexiones entre agentes, clientes y productos en una vista 3D interactiva'
                        : 'Explore connections between agents, clients and products in an interactive 3D view'
                    }
                </p>
                <div className="d-flex justify-content-center gap-3">
                    <small className="text-muted">
                        <FontAwesomeIcon icon={faRobot} className="text-primary me-1" />
                        {profiles.filter(p => p.isActive).length} {language === 'es' ? 'Agentes' : 'Agents'}
                    </small>
                    <small className="text-muted">
                        <FontAwesomeIcon icon={faUsers} className="text-success me-1" />
                        {botConversations.length} {language === 'es' ? 'Clientes' : 'Clients'}
                    </small>
                    <small className="text-muted">
                        <FontAwesomeIcon icon={faBoxOpen} className="text-warning me-1" />
                        {productsFromTab.length} {language === 'es' ? 'Productos' : 'Products'}
                    </small>
                </div>
            </div>
            <Button variant="primary" size="sm" className="w-100">
                <FontAwesomeIcon icon={faEye} className="me-2" />
                {language === 'es' ? 'Abrir Vista 3D' : 'Open 3D View'}
            </Button>
        </Card.Body>
    </Card>
</Col>
            </Row>

            {/* ==================== FILA 4: ACTIVIDAD RECIENTE Y SISTEMA ==================== */}
            <Row className="mb-4">
{/* CORRECCIÓN: Actividad Reciente - RESTAURADO DEL ORIGINAL */}
                <Col xl={6} lg={6} className="mb-4">
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Header className="bg-white border-0 pb-0">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-1">
                                        <FontAwesomeIcon icon={faClock} className="text-info me-2" />
                                        {language === 'es' ? 'Conversaciones Activas Recientes' : 'Recent Active Conversations'}
                                    </h5>
                                    <small className="text-muted">
                                        {language === 'es' ? 'Últimas 5 conversaciones con mayor potencial' : 'Top 5 conversations with highest potential'}
                                    </small>
                                </div>
                                <div className="d-flex gap-2">
                                    <Badge bg="primary" className="px-2 py-1">
                                        {botConversations.length} {language === 'es' ? 'conversaciones' : 'conversations'}
                                    </Badge>
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        onClick={() => setActiveTab('conversations')}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <FontAwesomeIcon icon={faEye} className="me-1" />
                                        {language === 'es' ? 'Ver Todas' : 'View All'}
                                    </Button>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {botConversations.length > 0 ? (
                                <Table hover responsive className="mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0 ps-3">{language === 'es' ? 'Contacto' : 'Contact'}</th>
                                            <th className="border-0">{language === 'es' ? 'Producto' : 'Product'}</th>
                                            <th className="border-0">{language === 'es' ? 'Etapa' : 'Stage'}</th>
                                            <th className="border-0">{language === 'es' ? 'Valor' : 'Value'}</th>
                                            <th className="border-0">{language === 'es' ? 'Probabilidad' : 'Probability'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {botConversations
                                            .sort((a, b) => b.probability - a.probability)
                                            .slice(0, 5)
                                            .map((conversation) => (
                                            <tr 
                                                key={conversation.id} 
                                                style={{ cursor: 'pointer' }} 
                                                onClick={() => {
                                                    setSelectedItem(conversation);
                                                    setShowConversationModal(true);
                                                }}
                                            >
                                                <td className="ps-3">
                                                    <div className="d-flex align-items-center">
                                                        <div 
                                                            className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                            style={{ 
                                                                width: '32px', 
                                                                height: '32px', 
                                                                background: 'linear-gradient(135deg, #0d6efd, #6f42c1)',
                                                                color: 'white',
                                                                fontSize: '14px',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {conversation.contactName?.charAt(0)?.toUpperCase() || 'C'}
                                                        </div>
                                                        <div>
                                                            <div className="fw-medium">{conversation.contactName}</div>
                                                            <small className="text-muted">{conversation.contactPhone}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="fw-medium">{conversation.productOffered}</span>
                                                </td>
                                                <td>
                                                    <Badge 
                                                        bg={getStageColor(conversation.stage)} 
                                                        className="rounded-pill"
                                                    >
                                                        {translateStage(conversation.stage, language)}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <span className="fw-bold text-success">
                                                        {formatCurrency(conversation.value || 0)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <span className="me-2 small">{conversation.probability}%</span>
                                                        <ProgressBar 
                                                            now={conversation.probability} 
                                                            style={{ height: '4px', flex: 1, maxWidth: '50px' }}
                                                            variant={
                                                                conversation.probability >= 75 ? 'success' :
                                                                conversation.probability >= 50 ? 'warning' : 'danger'
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-4">
                                    <FontAwesomeIcon icon={faComments} size="2x" className="text-muted opacity-50 mb-2" />
                                    <p className="text-muted mb-0">
                                        {language === 'es' ? 'No hay conversaciones activas' : 'No active conversations'}
                                    </p>
                                    <small className="text-muted d-block mt-2">
                                        {realConversationsError && (
                                            <span className="text-warning">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                                {realConversationsError}
                                            </span>
                                        )}
                                    </small>
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        className="mt-2"
                                        onClick={() => setActiveTab('conversations')}
                                    >
                                        <FontAwesomeIcon icon={faEye} className="me-1" />
                                        {language === 'es' ? 'Ir a Conversaciones' : 'Go to Conversations'}
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* System Status & Metrics - 50% */}
                <Col xl={6} lg={6} className="mb-4">
                    <Card 
                        className="h-100 border-0 shadow-sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem({ type: 'system', data: 'system_status' })}
                    >
                        <Card.Header className="bg-white border-0 pb-0">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-1">
                                        <FontAwesomeIcon icon={faNetworkWired} className="text-success me-2" />
                                        {language === 'es' ? 'Estado del Sistema' : 'System Status'}
                                    </h5>
                                    <small className="text-muted">{language === 'es' ? 'Componentes en tiempo real' : 'Real-time components'}</small>
                                </div>
                                <Button variant="outline-success" size="sm">
                                    <FontAwesomeIcon icon={faEye} />
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0">
                                            <div className={`p-2 rounded ${sellerbotStatus?.success ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}`}>
                                                <FontAwesomeIcon 
                                                    icon={faRobot} 
                                                    className={sellerbotStatus?.success ? 'text-success' : 'text-danger'} 
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <div className="fw-medium">{language === 'es' ? 'Bot de Ventas' : 'Sales Bot'}</div>
                                            <small className={sellerbotStatus?.success ? 'text-success' : 'text-danger'}>
                                                {sellerbotStatus?.success ? 
                                                    (language === 'es' ? 'Operativo' : 'Online') : 
                                                    (language === 'es' ? 'Desconectado' : 'Offline')
                                                }
                                            </small>
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6} className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0">
                                            <div className="bg-primary bg-opacity-10 p-2 rounded">
                                                <FontAwesomeIcon icon={faBullseye} className="text-primary" />
                                            </div>
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <div className="fw-medium">{language === 'es' ? 'Campañas' : 'Campaigns'}</div>
                                            <small className="text-muted">
                                                {campaigns.filter(c => c.status === 'running').length} {language === 'es' ? 'activas' : 'active'}
                                            </small>
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6} className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0">
                                            <div className="bg-warning bg-opacity-10 p-2 rounded">
                                                <FontAwesomeIcon icon={faClock} className="text-warning" />
                                            </div>
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <div className="fw-medium">{language === 'es' ? 'Rutinas' : 'Routines'}</div>
                                            <small className="text-muted">
                                                {routinesFromTab.filter(r => r.isActive).length} {language === 'es' ? 'activas' : 'active'}
                                            </small>
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6} className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0">
                                            <div className="bg-info bg-opacity-10 p-2 rounded">
                                                <FontAwesomeIcon icon={faUserTie} className="text-info" />
                                            </div>
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <div className="fw-medium">{language === 'es' ? 'Agentes IA' : 'AI Agents'}</div>
                                            <small className="text-muted">
                                                {profiles.filter(p => p.isActive).length} {language === 'es' ? 'activos' : 'active'}
                                            </small>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <hr className="my-3" />

                            {/* Quick Stats */}
                            <Row className="text-center">
                                <Col md={3}>
                                    <div className="border-end">
                                        <h6 className="text-primary mb-0">{formatNumber(dashboardMetrics.totalLeads)}</h6>
                                        <small className="text-muted">{language === 'es' ? 'Leads Total' : 'Total Leads'}</small>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="border-end">
                                        <h6 className="text-success mb-0">{dashboardMetrics.closedDeals}</h6>
                                        <small className="text-muted">{language === 'es' ? 'Cerrados' : 'Closed'}</small>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="border-end">
                                        <h6 className="text-warning mb-0">{dashboardMetrics.activeDeals}</h6>
                                        <small className="text-muted">{language === 'es' ? 'Activos' : 'Active'}</small>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <h6 className="text-info mb-0">{dashboardMetrics.conversionRate.toFixed(1)}%</h6>
                                    <small className="text-muted">{language === 'es' ? 'Conversión' : 'Conversion'}</small>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ==================== MODALES EXPANDIBLES ==================== */}

            {/* ==================== MODAL DE VISTA 3D ==================== */}
<Modal 
    show={selectedItem && selectedItem.type === 'network3d'} 
    onHide={() => setSelectedItem(null)} 
    size="xl" 
    fullscreen="xl-down"
    centered
    className="network-3d-modalx"
>
    <Modal.Header closeButton className="bg-body text-body border-0">
        <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faNetworkWired} className="me-2 text-info" />
            {language === 'es' ? 'Red 3D de Conversaciones' : '3D Conversation Network'}
        </Modal.Title>
        <div className="ms-auto me-3 d-flex gap-2">
            <Badge bg="info" className="px-2 py-1">
                <FontAwesomeIcon icon={faRobot} className="me-1" />
                {profiles.filter(p => p.isActive).length} {language === 'es' ? 'Agentes' : 'Agents'}
            </Badge>
            <Badge bg="success" className="px-2 py-1">
                <FontAwesomeIcon icon={faUsers} className="me-1" />
                {botConversations.length} {language === 'es' ? 'Clientes' : 'Clients'}
            </Badge>
            <Badge bg="warning" className="px-2 py-1">
                <FontAwesomeIcon icon={faBoxOpen} className="me-1" />
                {productsFromTab.length} {language === 'es' ? 'Productos' : 'Products'}
            </Badge>
        </div>
    </Modal.Header>
    
    {/* 🔧 CORRECCIÓN CRÍTICA: Modal.Body con altura fija y flex */}
    <Modal.Body 
        className="p-0" 
        style={{ 
            height: '80vh', 
            backgroundColor: '#0a0a0a',
            display: 'flex',        // ✅ AGREGAR: Flex layout
            flexDirection: 'column', // ✅ AGREGAR: Columna flex
            minHeight: '600px'      // ✅ AGREGAR: Altura mínima
        }}
    >
        {selectedItem?.type === 'network3d' && (
            <div 
                className="position-relative" 
                style={{ 
                    flex: 1,           // ✅ CAMBIO: flex: 1 en lugar de h-100
                    display: 'flex',   // ✅ AGREGAR: Flex para el contenedor
                    flexDirection: 'column' // ✅ AGREGAR: Columna flex
                }}
            >
                {/* USAR ConversationNetwork EXISTENTE CON force3DMode */}
                <ConversationNetwork
                    conversations={botConversations.map(conv => ({
                        ...conv,
                        isActive: Boolean(conv.isActive)
                    }))}
                    loading={realConversationsLoading || conversationsLoading}
                    onRefresh={() => {
                        refetchRealConversations();
                        refetchConversations();
                        refetchStatus();
                    }}
                    language={language}
                    userConfig={{
                        whatsAppServerPort: 8000,
                        whatsAppApiPort: 8001,
                        mlServerPort: 8002
                    }}
                    force3DMode={true} // NUEVO: Activar modo 3D forzado
                />

                {/* Overlay de controles 3D */}
                <div className="position-relative top-0 end-0 m-3" style={{ zIndex: 10 }}>
                    <div className="bg-dark bg-opacity-75 text-white rounded p-3 shadow">
                        <h6 className="text-info mb-2">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            {language === 'es' ? 'Controles 3D' : '3D Controls'}
                        </h6>
                        <div className="small">
                            <div className="mb-1">
                                <FontAwesomeIcon icon={faGlobe} className="me-2 text-primary" />
                                {language === 'es' ? 'Arrastrar: Rotar vista' : 'Drag: Rotate view'}
                            </div>
                            <div className="mb-1">
                                <FontAwesomeIcon icon={faEye} className="me-2 text-success" />
                                {language === 'es' ? 'Scroll: Zoom in/out' : 'Scroll: Zoom in/out'}
                            </div>
                            <div className="mb-1">
                                <FontAwesomeIcon icon={faComments} className="me-2 text-warning" />
                                {language === 'es' ? 'Click: Ver conversación' : 'Click: View conversation'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading overlay para 3D */}
                {(realConversationsLoading || conversationsLoading) && (
                    <div 
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ 
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            zIndex: 10
                        }}
                    >
                        <div className="text-center text-white">
                            <Spinner animation="border" className="mb-3" style={{ width: '4rem', height: '4rem' }} />
                            <div className="h4">
                                {language === 'es' ? 'Construyendo Red 3D...' : 'Building 3D Network...'}
                            </div>
                            <div className="text-muted">
                                {language === 'es' 
                                    ? 'Procesando conversaciones y conexiones' 
                                    : 'Processing conversations and connections'}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )}
    </Modal.Body>
    
    <Modal.Footer className="bg-dark border-0">
        <div className="d-flex justify-content-between align-items-center w-100">
            <div className="text-white-50 small">
                <FontAwesomeIcon icon={faClock} className="me-1" />
                {language === 'es' ? 'Última actualización:' : 'Last updated:'} {lastUpdate.toLocaleTimeString()}
            </div>
            <div className="d-flex gap-2">
                <Button 
                    variant="outline-info" 
                    size="sm"
                    onClick={() => {
                        refetchRealConversations();
                        refetchConversations();
                    }}
                    disabled={realConversationsLoading || conversationsLoading}
                >
                    <FontAwesomeIcon 
                        icon={faSync} 
                        className={realConversationsLoading || conversationsLoading ? 'fa-spin me-1' : 'me-1'} 
                    />
                    {language === 'es' ? 'Actualizar' : 'Refresh'}
                </Button>
                <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => setSelectedItem(null)}
                >
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    {language === 'es' ? 'Cerrar' : 'Close'}
                </Button>
            </div>
        </div>
    </Modal.Footer>
</Modal>

<style>{`

    /* Estilos para Modal 3D */
  .network-3d-modal .modal-dialog {
    max-width: 95vw !important;
    margin: 1rem auto !important;
    height: calc(100vh - 2rem) !important; /* ✅ AGREGAR: Altura fija */
  }
  
  .network-3d-modal .modal-content {
    height: 100% !important;                /* ✅ CAMBIO: Altura completa */
    border-radius: 15px !important;
    overflow: hidden !important;
    background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%) !important;
    display: flex !important;               /* ✅ AGREGAR: Flex layout */
    flex-direction: column !important;      /* ✅ AGREGAR: Columna flex */
  }
  
  .network-3d-modal .modal-header {
    background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%) !important;
    border-bottom: 2px solid #34495e !important;
    flex-shrink: 0 !important;              /* ✅ AGREGAR: No encoger */
  }
  
  .network-3d-modal .modal-footer {
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%) !important;
    border-top: 2px solid #34495e !important;
    flex-shrink: 0 !important;              /* ✅ AGREGAR: No encoger */
  }
  
  .network-3d-modal .modal-body {
    padding: 0 !important;
    overflow: hidden !important;
    flex: 1 !important;                     /* ✅ AGREGAR: Ocupar espacio disponible */
    display: flex !important;               /* ✅ AGREGAR: Flex layout */
    flex-direction: column !important;      /* ✅ AGREGAR: Columna flex */
  }
  
  /* Animaciones para los overlays */
  .network-3d-modal .position-absolute > div {
    animation: fadeInUp 0.5s ease-out !important;
    backdrop-filter: blur(10px) !important;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Responsive para móviles */
  @media (max-width: 768px) {
    .network-3d-modal .modal-dialog {
      margin: 0.5rem !important;
      height: calc(100vh - 1rem) !important; /* ✅ AJUSTAR: Para móviles */
    }
    
    .network-3d-modal .position-absolute.top-0.end-0 {
      top: 10px !important;
      right: 10px !important;
      margin: 0 !important;
    }
    
    .network-3d-modal .position-absolute > div {
      padding: 0.75rem !important;
      font-size: 0.85rem !important;
    }
  }
`}</style>

            {/* Modal expandible para gráficos */}
            <Modal 
                show={selectedItem && selectedItem.type === 'chart'} 
                onHide={() => setSelectedItem(null)} 
                size="xl" 
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
                        {selectedItem?.data === 'revenue_timeline' && (language === 'es' ? 'Tendencia de Ingresos Detallada' : 'Detailed Revenue Trend')}
                        {selectedItem?.data === 'deal_distribution' && (language === 'es' ? 'Distribución de Deals' : 'Deal Distribution')}
                        {selectedItem?.data === 'agent_performance' && (language === 'es' ? 'Performance de Agentes' : 'Agent Performance')}
                        {selectedItem?.data === 'sales_funnel' && (language === 'es' ? 'Embudo de Ventas Detallado' : 'Detailed Sales Funnel')}
                        {selectedItem?.data === 'conversion_gauge' && (language === 'es' ? 'Medidor de Conversión' : 'Conversion Gauge')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ minHeight: '500px' }}>
                    {selectedItem?.data === 'revenue_timeline' && (
                        <ResponsiveContainer width="100%" height={400}>
                            <AreaChart data={selectedItem.chartData}>
                                <defs>
                                    <linearGradient id="colorRevenueModal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#0d6efd" stopOpacity={0.1}/>
                                    </linearGradient>
                                    <linearGradient id="colorLeadsModal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#20c997" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#20c997" stopOpacity={0.1}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip 
                                    formatter={(value, name) => [
                                        name === 'revenue' ? formatCurrency(value as number) : formatNumber(value as number),
                                        name === 'revenue' ? 'Revenue' : 'Leads'
                                    ]}
                                />
                                <Legend />
                                <Area type="monotone" dataKey="revenue" stroke="#0d6efd" fillOpacity={1} fill="url(#colorRevenueModal)" />
                                <Area type="monotone" dataKey="leads" stroke="#20c997" fillOpacity={1} fill="url(#colorLeadsModal)" />
                                <Area type="monotone" dataKey="conversions" stroke="#ffc107" fillOpacity={1} fill="#ffc107" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}

                    {selectedItem?.data === 'deal_distribution' && (
                        <ResponsiveContainer width="100%" height={400}>
                            <PieChart>
                                <Pie
                                    data={selectedItem.chartData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={120}
                                    fill="#8884d8"
                                    dataKey="count"
                                >
                                    {selectedItem.chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={
                                            index === 0 ? '#0d6efd' :
                                            index === 1 ? '#20c997' :
                                            index === 2 ? '#ffc107' :
                                            index === 3 ? '#fd7e14' : '#dc3545'
                                        } />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    )}

                    {selectedItem?.data === 'agent_performance' && (
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={selectedItem.chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="agent" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="leads" fill="#20c997" name="Leads" />
                                <Bar dataKey="conversions" fill="#0d6efd" name="Conversions" />
                                <Bar dataKey="revenue" fill="#ffc107" name="Revenue (K)" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                    {selectedItem?.data === 'conversion_gauge' && (
                        <div className="text-center">
                            <div className="d-inline-block position-relative">
                                <ResponsiveContainer width={300} height={300}>
                                    <PieChart>
                                        <Pie
                                            data={[
                                                { name: 'Converted', value: selectedItem.value },
                                                { name: 'Remaining', value: 100 - selectedItem.value }
                                            ]}
                                            cx="50%"
                                            cy="50%"
                                            startAngle={90}
                                            endAngle={-270}
                                            innerRadius={80}
                                            outerRadius={120}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            <Cell fill="#28a745" />
                                            <Cell fill="#e9ecef" />
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="position-absolute top-50 start-50 translate-middle text-center">
                                    <h2 className="mb-0 fw-bold">{selectedItem.value.toFixed(1)}%</h2>
                                    <small className="text-muted">{language === 'es' ? 'Conversión' : 'Conversion'}</small>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedItem(null)}>
                        {language === 'es' ? 'Cerrar' : 'Close'}
                    </Button>
                    <Button variant="primary">
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        {language === 'es' ? 'Exportar' : 'Export'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal expandible para KPIs */}
            <Modal 
                show={selectedItem && selectedItem.type === 'kpi'} 
                onHide={() => setSelectedItem(null)} 
                size="lg" 
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faChartPie} className="me-2 text-primary" />
                        {selectedItem?.data === 'totalRevenue' && (language === 'es' ? 'Análisis de Revenue Total' : 'Total Revenue Analysis')}
                        {selectedItem?.data === 'mrr' && (language === 'es' ? 'Monthly Recurring Revenue' : 'Monthly Recurring Revenue')}
                        {selectedItem?.data === 'avgDeal' && (language === 'es' ? 'Tamaño Promedio de Deal' : 'Average Deal Size')}
                        {selectedItem?.data === 'conversion' && (language === 'es' ? 'Tasa de Conversión' : 'Conversion Rate')}
                        {selectedItem?.data === 'activeDeals' && (language === 'es' ? 'Deals Activos' : 'Active Deals')}
                        {selectedItem?.data === 'velocity' && (language === 'es' ? 'Velocidad de Ventas' : 'Sales Velocity')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={4}>
                            <Card className="border-0 bg-light h-100">
                                <Card.Body className="text-center">
                                    <h2 className="text-primary mb-2">
                                        {selectedItem?.data === 'velocity' 
                                            ? selectedItem.value
                                            : selectedItem?.data === 'conversion' 
                                                ? `${selectedItem.value.toFixed(1)}%`
                                                : formatCurrency(selectedItem?.value || 0)
                                        }
                                    </h2>
                                    <p className="text-muted mb-0">{language === 'es' ? 'Valor Actual' : 'Current Value'}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={8}>
                            <div className="mb-3">
                                <h6>{language === 'es' ? 'Tendencia (último mes)' : 'Trend (last month)'}</h6>
                                <ResponsiveContainer width="100%" height={150}>
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" hide />
                                        <YAxis hide />
                                        <Tooltip />
                                        <Line 
                                            type="monotone" 
                                            dataKey={selectedItem?.data === 'velocity' ? 'deals' : 'revenue'} 
                                            stroke="#0d6efd" 
                                            strokeWidth={2}
                                            dot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            
                            <div className="row g-3">
                                <div className="col-6">
                                    <div className="text-center p-2 bg-success bg-opacity-10 rounded">
                                        <small className="text-success fw-bold">+12.5%</small>
                                        <div><small className="text-muted">{language === 'es' ? 'vs mes anterior' : 'vs last month'}</small></div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="text-center p-2 bg-info bg-opacity-10 rounded">
                                        <small className="text-info fw-bold">#{Math.floor(Math.random() * 10) + 1}</small>
                                        <div><small className="text-muted">{language === 'es' ? 'Ranking' : 'Ranking'}</small></div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedItem(null)}>
                        {language === 'es' ? 'Cerrar' : 'Close'}
                    </Button>
                    <Button variant="primary">
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        {language === 'es' ? 'Exportar Datos' : 'Export Data'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )}
</Tab.Pane>
                            {/* ==================== PESTAÑA 2: CONVERSACIONES ==================== */}
                            <Tab.Pane eventKey="conversations">
                                <div className="conversations-network-container h-100">
                                    {/* Header de la pestaña */}
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <h2 className="mb-1 fw-bold">
                                                <FontAwesomeIcon icon={faComments} className="me-3 text-primary" />
                                                {language === 'es' ? 'Red de Conversaciones' : 'Conversation Network'}
                                            </h2>
                                            <p className="text-muted mb-0">
                                                {language === 'es' 
                                                    ? 'Supervisión en tiempo real de todas las conversaciones del bot' 
                                                    : 'Real-time supervision of all bot conversations'}
                                            </p>
                                        </div>
                                        <div className="d-flex gap-2">
                                            {/* Status del bot */}
                                            <Badge 
                                                bg={sellerbotStatus?.data?.enabled ? 'success' : 'secondary'} 
                                                className="d-flex align-items-center py-2 px-3"
                                                style={{ borderRadius: '12px' }}
                                            >
                                                <FontAwesomeIcon 
                                                    icon={sellerbotStatus?.data?.enabled ? faRobot : faPause} 
                                                    className="me-2" 
                                                />
                                                {sellerbotStatus?.data?.enabled ? 
                                                    (language === 'es' ? 'Bot Activo' : 'Bot Active') :
                                                    (language === 'es' ? 'Bot Inactivo' : 'Bot Inactive')
                                                }
                                            </Badge>
                                            
                                            {/* Contador de conversaciones */}
                                            <Badge bg="info" className="d-flex align-items-center py-2 px-3" style={{ borderRadius: '12px' }}>
                                                <FontAwesomeIcon icon={faNetworkWired} className="me-2" />
                                                {botConversations.length} {language === 'es' ? 'conversaciones' : 'conversations'}
                                            </Badge>
                                            
                                            {/* Toggle del bot */}
                                            <Button
                                                size="sm"
                                                variant={sellerbotStatus?.data?.enabled ? 'outline-warning' : 'outline-success'}
                                                onClick={async () => {
                                                    try {
                                                        const newEnabled = !sellerbotStatus?.data?.enabled;
                                                        await SellerbotService.toggleBot(newEnabled, 'admin');
                                                        
                                                        refetchStatus();
                                                        refetchRealConversations();
                                                        
                                                        setSuccess(language === 'es' 
                                                            ? `Bot ${newEnabled ? 'activado' : 'desactivado'} correctamente`
                                                            : `Bot ${newEnabled ? 'enabled' : 'disabled'} successfully`);
                                                        
                                                        setTimeout(() => setSuccess(null), 3000);
                                                        
                                                    } catch (error: any) {
                                                        setError(error.message || (language === 'es' ? 'Error al cambiar estado del bot' : 'Error changing bot status'));
                                                        setTimeout(() => setError(null), 5000);
                                                    }
                                                }}
                                                disabled={statusLoading}
                                                style={{ borderRadius: '12px' }}
                                            >
                                                <FontAwesomeIcon 
                                                    icon={sellerbotStatus?.data?.enabled ? faPause : faPlay} 
                                                    className="me-2" 
                                                />
                                                {sellerbotStatus?.data?.enabled ? 
                                                    (language === 'es' ? 'Pausar Bot' : 'Pause Bot') :
                                                    (language === 'es' ? 'Activar Bot' : 'Activate Bot')
                                                }
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Alertas de estado */}
                                    {!sellerbotStatus?.data?.enabled && (
                                        <Alert variant="warning" className="mb-3" style={{ borderRadius: '12px', border: 'none' }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                            <strong>{language === 'es' ? 'Bot Desactivado:' : 'Bot Disabled:'}</strong>
                                            {' '}
                                            {language === 'es' 
                                                ? 'El bot no está procesando nuevas conversaciones. Active el bot para comenzar la supervisión automática.'
                                                : 'The bot is not processing new conversations. Enable the bot to start automatic supervision.'}
                                        </Alert>
                                    )}

                                    {realConversationsError && (
                                        <Alert variant="warning" className="mb-3" style={{ borderRadius: '12px', border: 'none' }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                            <strong>{language === 'es' ? 'Error cargando conversaciones:' : 'Error loading conversations:'}</strong>
                                            {' '}{realConversationsError}
                                        </Alert>
                                    )}

                                    {/* CORRECCIÓN: Usar conversaciones reales para ConversationNetwork */}
                                    <div className="conversation-network-wrapper" style={{ height: 'calc(100vh - 300px)', minHeight: '600px' }}>
                                        <ConversationNetwork
                                            conversations={botConversations.map(conv => ({
                                                ...conv,
                                                isActive: Boolean(conv.isActive) // Ensure isActive is always boolean
                                            }))}
                                            loading={realConversationsLoading || conversationsLoading}
                                            onRefresh={() => {
                                                refetchRealConversations();
                                                refetchConversations();
                                                refetchStatus();
                                            }}
                                            language={language}
                                        />
                                    </div>

                                    {/* Footer con métricas */}
                                    <div className="mt-4 p-4 rounded-3" style={{ background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)' }}>
                                        <Row className="g-3 align-items-center">
                                            <Col md={3}>
                                                <div className="text-center">
                                                    <h4 className="mb-0 text-primary fw-bold">
                                                        {botConversations.filter(c => c.isActive).length}
                                                    </h4>
                                                    <small className="text-muted fw-semibold">
                                                        {language === 'es' ? 'Conversaciones Activas' : 'Active Conversations'}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className="text-center">
                                                    <h4 className="mb-0 text-warning fw-bold">
                                                        {botConversations.filter(c => c.stage === 'negotiation').length}
                                                    </h4>
                                                    <small className="text-muted fw-semibold">
                                                        {language === 'es' ? 'En Negociación' : 'In Negotiation'}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className="text-center">
                                                    <h4 className="mb-0 text-success fw-bold">
                                                        {botConversations.filter(c => c.stage === 'closed').length}
                                                    </h4>
                                                    <small className="text-muted fw-semibold">
                                                        {language === 'es' ? 'Ventas Cerradas' : 'Closed Sales'}
                                                    </small>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className="text-center">
                                                    <h4 className="mb-0 text-info fw-bold">
                                                        ${botConversations.reduce((sum, c) => sum + (c.stage === 'closed' ? c.value : 0), 0).toLocaleString()}
                                                    </h4>
                                                    <small className="text-muted fw-semibold">
                                                        {language === 'es' ? 'Revenue Total' : 'Total Revenue'}
                                                    </small>
                                                </div>
                                            </Col>
                                        </Row>
                                        
                                        <hr className="my-3" />
                                        
                                        <Row className="align-items-center">
                                            <Col>
                                                <div className="d-flex gap-4 small text-muted">
                                                    <span>
                                                        <FontAwesomeIcon icon={faClock} className="me-1 text-primary" />
                                                        {language === 'es' ? 'Última actualización:' : 'Last updated:'} {lastUpdate.toLocaleTimeString()}
                                                    </span>
                                                    <span>
                                                        <FontAwesomeIcon icon={faNetworkWired} className="me-1 text-info" />
                                                        {language === 'es' ? 'Tiempo real:' : 'Real-time:'} 
                                                        {globalAutoRefresh ? 
                                                            (language === 'es' ? ' Activo' : ' Active') : 
                                                            (language === 'es' ? ' Pausado' : ' Paused')
                                                        }
                                                    </span>
                                                    <span>
                                                        <FontAwesomeIcon icon={faRobot} className="me-1 text-success" />
                                                        {language === 'es' ? 'Bot:' : 'Bot:'} 
                                                        {sellerbotStatus?.data?.enabled ? 
                                                            (language === 'es' ? ' Operativo' : ' Operational') :
                                                            (language === 'es' ? ' Detenido' : ' Stopped')
                                                        }
                                                    </span>
                                                    <span>
                                                        <FontAwesomeIcon icon={faComments} className="me-1 text-warning" />
                                                        {language === 'es' ? 'Fuente:' : 'Source:'} 
                                                        {realConversations.length > 0 ? 
                                                            (language === 'es' ? ' Datos Reales' : ' Real Data') :
                                                            (language === 'es' ? ' Datos Demo' : ' Demo Data')
                                                        }
                                                    </span>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            </Tab.Pane>

                            {/* ==================== RESTO DE PESTAÑAS ==================== */}
                            <Tab.Pane eventKey="products">
                                <ProductsTab 
                                    language={language}
                                    onProductsChange={handleProductsChange}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="agents">
                                <AgentsTab 
                                    language={language}
                                    onAgentsChange={handleAgentsChange}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="campaigns">
                                <CampaignsTab 
                                    language={language}
                                    onCampaignsChange={handleCampaignsChange}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="contacts">
                                <ContactsTab 
                                    language={language}
                                    onContactsChange={handleContactsChange}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="routines">
                                <RoutinesTab 
                                    language={language}
                                    onRoutinesChange={handleRoutinesChange}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="settings">
                                <SettingsTab 
                                    language={language}
                                    onSettingsChange={(settings) => {
                                        if (settings.language !== language) {
                                            setLanguage(settings.language);
                                        }
                                        if (settings.globalAutoRefresh !== globalAutoRefresh) {
                                            setGlobalAutoRefresh(settings.globalAutoRefresh);
                                        }
                                    }}
                                    totalConversations={botConversations.length}
                                    totalProducts={productsFromTab.filter(p => p.isActive).length}
                                    totalProfiles={profiles.filter(p => p.isActive).length}
                                    totalCampaigns={campaigns.filter(c => c.status === 'running').length}
                                    botConversations={botConversations}
                                />
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>

                    {/* ==================== MODAL DE CONVERSACIÓN DETALLADA ==================== */}
                    <Modal show={showConversationModal} onHide={() => setShowConversationModal(false)} size="lg" centered>
                        <Modal.Header closeButton style={{ borderRadius: '20px 20px 0 0', border: 'none' }}>
                            <Modal.Title className="fw-bold">
                                <FontAwesomeIcon icon={faComments} className="me-2 text-primary" />
                                {language === 'es' ? 'Detalle de Conversación' : 'Conversation Details'}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="p-4">
                            {selectedItem && (
                                <div>
                                    <Row className="mb-4">
                                        <Col md={6}>
                                            <div className="p-3 rounded-3" style={{ background: 'linear-gradient(135deg, #e3f2fd, #f3e5f5)' }}>
                                                <h6 className="fw-bold mb-3">
                                                    <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                                                    {language === 'es' ? 'Información del Contacto' : 'Contact Information'}
                                                </h6>
                                                <div className="mb-2">
                                                    <strong>{language === 'es' ? 'Contacto:' : 'Contact:'}</strong> {selectedItem.contactName}
                                                </div>
                                                <div className="mb-2">
                                                    <strong>{language === 'es' ? 'Teléfono:' : 'Phone:'}</strong> {selectedItem.contactPhone}
                                                </div>
                                                <div className="mb-0">
                                                    <strong>{language === 'es' ? 'Email:' : 'Email:'}</strong> {selectedItem.contactEmail || 'N/A'}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="p-3 rounded-3" style={{ background: 'linear-gradient(135deg, #e8f5e8, #fff3e0)' }}>
                                                <h6 className="fw-bold mb-3">
                                                    <FontAwesomeIcon icon={faBoxOpen} className="me-2 text-success" />
                                                    {language === 'es' ? 'Detalles de Venta' : 'Sales Details'}
                                                </h6>
                                                <div className="mb-2">
                                                    <strong>{language === 'es' ? 'Producto:' : 'Product:'}</strong> {selectedItem.productOffered}
                                                </div>
                                                <div className="mb-2">
                                                    <strong>{language === 'es' ? 'Agente:' : 'Agent:'}</strong> {selectedItem.profileUsed}
                                                </div>
                                                <div className="mb-0">
                                                    <strong>{language === 'es' ? 'Valor:' : 'Value:'}</strong> 
                                                    <span className="text-success fw-bold ms-2">{formatCurrency(selectedItem.value)}</span>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    
                                    <div className="mb-4">
                                        <h6 className="fw-bold mb-2">
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
                                            {language === 'es' ? 'Estado y Progreso' : 'Status & Progress'}
                                        </h6>
                                        <div className="d-flex align-items-center gap-3 mb-3">
                                            <Badge 
                                                bg={getStageColor(selectedItem.stage)}
                                                className="px-3 py-2 rounded-pill"
                                                style={{ fontSize: '0.9rem' }}
                                            >
                                                {translateStage(selectedItem.stage, language)}
                                            </Badge>
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between mb-1">
                                                    <small className="text-muted">{language === 'es' ? 'Probabilidad de cierre' : 'Close probability'}</small>
                                                    <small className="fw-bold">{selectedItem.probability}%</small>
                                                </div>
                                                <ProgressBar 
                                                    now={selectedItem.probability} 
                                                    style={{ height: '8px', borderRadius: '4px' }}
                                                    variant={
                                                        selectedItem.probability >= 75 ? 'success' :
                                                        selectedItem.probability >= 50 ? 'warning' : 'danger'
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h6 className="fw-bold mb-2">
                                            <FontAwesomeIcon icon={faHistory} className="me-2 text-warning" />
                                            {language === 'es' ? 'Notas y Observaciones' : 'Notes & Observations'}
                                        </h6>
                                        <div className="p-3 bg-light rounded-3">
                                            {selectedItem.notes || (language === 'es' ? 'Sin notas adicionales disponibles.' : 'No additional notes available.')}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <h6 className="fw-bold mb-3">
                                            <FontAwesomeIcon icon={faComments} className="me-2 text-primary" />
                                            {language === 'es' ? 'Historial de Conversación' : 'Conversation History'}
                                        </h6>
                                        <div 
                                            className="border rounded-3 p-3" 
                                            style={{ 
                                                maxHeight: '300px', 
                                                overflowY: 'auto',
                                                background: 'linear-gradient(135deg, #fafafa, #f0f0f0)'
                                            }}
                                        >
                                            {selectedItem.messages && selectedItem.messages.length > 0 ? (
                                                selectedItem.messages.map((msg: ConversationMessage) => (
                                                    <div key={msg.id} className={`mb-3 ${msg.sender === 'bot' ? 'text-end' : ''}`}>
                                                        <div 
                                                            className={`d-inline-block p-3 rounded-3 ${
                                                                msg.sender === 'bot' 
                                                                    ? 'text-white' 
                                                                    : 'bg-white border text-dark'
                                                            }`}
                                                            style={{ 
                                                                maxWidth: '80%',
                                                                background: msg.sender === 'bot' 
                                                                    ? 'linear-gradient(135deg, #0d6efd, #6f42c1)'
                                                                    : 'white'
                                                            }}
                                                        >
                                                            <div className="mb-1">{msg.message}</div>
                                                            <small className={msg.sender === 'bot' ? 'text-white-50' : 'text-muted'}>
                                                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                                                {formatDate(msg.timestamp, language)}
                                                            </small>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center py-4">
                                                    <FontAwesomeIcon icon={faComments} size="2x" className="mb-2 text-muted opacity-50" />
                                                    <p className="text-muted mb-0">
                                                        {language === 'es' ? 'No hay mensajes disponibles en esta conversación.' : 'No messages available in this conversation.'}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer style={{ borderRadius: '0 0 20px 20px', border: 'none' }}>
                            <Button 
                                variant="secondary" 
                                onClick={() => setShowConversationModal(false)}
                                style={{ borderRadius: '12px' }}
                            >
                                {language === 'es' ? 'Cerrar' : 'Close'}
                            </Button>
                            <Button 
                                variant="primary" 
                                style={{ 
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #0d6efd, #6f42c1)',
                                    border: 'none'
                                }}
                            >
                                <FontAwesomeIcon icon={faComments} className="me-2" />
                                {language === 'es' ? 'Continuar Chat' : 'Continue Chat'}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
            </div>
        </div>
    );
};

export default AISeller;

// ==================== 📋 RESUMEN DE CORRECCIONES APLICADAS ====================

/*
🔧 CORRECCIONES APLICADAS:

✅ 1. MANTENER IMPORTS ORIGINALES:
   - No cambiar imports de sellerbotService.ts
   - Importar SellerConvService para conversaciones reales

✅ 2. HOOK PERSONALIZADO PARA CONVERSACIONES REALES:
   - Crear estados para realConversations
   - Función fetchRealConversations usando SellerConvService
   - Auto-refresh cada 5 minutos

✅ 3. LÓGICA DE CONVERSACIONES HÍBRIDA:
   - Usar conversaciones reales si están disponibles
   - Fallback a conversaciones de sellerbotService
   - Mapeo correcto a formato estándar

✅ 4. CORRECCIÓN EN ACTIVIDAD RECIENTE:
   - Mostrar error si hay problemas cargando datos reales
   - Indicador de fuente de datos (Real/Demo)
   - Funcionalidad completa de tabla

✅ 5. MEJORAS EN ESTADOS DE LOADING:
   - Loading combinado para ambas fuentes
   - Estados de error específicos
   - Indicadores de estado del sistema

✅ 6. INTEGRACIÓN CON CONVERSATIONNETWORK:
   - Pasar conversaciones correctas a ConversationNetwork
   - Refresh de ambas fuentes
   - Estados sincronizados

📊 RESULTADO ESPERADO:
- Dashboard muestra conversaciones reales si están disponibles
- Fallback automático a datos demo si hay errores
- Actividad Reciente funcional con datos reales
- Indicadores claros de estado y fuente de datos
- Manejo robusto de errores

🎯 FUNCIONALIDADES GARANTIZADAS:
- ✅ Conversaciones reales desde BD de contactos
- ✅ Filtrado por números de teléfono
- ✅ Fallback a datos demo
- ✅ Estados de error claros
- ✅ Auto-refresh configurable
- ✅ Integración completa con pestañas
*/